import { Dialog, Button } from "@mui/material";
import { getLayerCounter, getLegends } from "api/layer";
import { ReactComponent as LocationsSvg } from "assets/icon_locations.svg";
import Options from "components/options/options";
import SubOptions from "components/options/sub-options";
import Svg from "components/svg/svg";
import { RightPanel, Option } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { EventData } from "helpers/models";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./single-analyse-modal.module.scss";
import SelectEvent from "../select-event/select-event";
import SelectLayer from "../select-layer/select-layer";
import {
    buildClimateRiskScores,
    buildFutureHazardRiskScores,
    buildCrsComponentRiskScores,
    CRS_RESPONSE_STRUCTURE,
    FATHOM_LAYER_IDS,
} from "../shared/analysis-shared";
import {
    executeSingleAnalysisWithReporting,
    syncSingleAnalysisRunningStatus,
    SingleAnalysisParams,
} from "../shared/single-analysis-executor";

/** 单点分析弹窗：处理 rightPanel === Single 的分析流程 */
function SingleAnalyseModal() {
    type SingleSubOption =
        | "natural-hazard"
        | "climate-risk-score"
        | "future-climate-risk"
        | "climate-risk-variable";

    const NATURAL_HAZARD_SUB_OPTION: SingleSubOption = "natural-hazard";
    const CLIMATE_RISK_SCORE_SUB_OPTION: SingleSubOption = "climate-risk-score";
    const FUTURE_CLIMATE_RISK_SUB_OPTION: SingleSubOption = "future-climate-risk";
    const CLIMATE_RISK_VARIABLE_SUB_OPTION: SingleSubOption = "climate-risk-variable";

    const [context, dispatch] = useContext(AppContext);
    const [option, setOption] = useState<Option>(Option.HAZARD);
    const [subOption, setSubOption] = useState<SingleSubOption>(
        NATURAL_HAZARD_SUB_OPTION
    );
    const [t] = useTranslation();
    const [fathomValidateStatus, setFathomValidateStatus] = useState(false);
    const [hazardValidateStatus, setHazardValidateStatus] = useState(false);
    const [crsValidateStatus, setCrsValidateStatus] = useState(false);
    const [hazardButtons, setHazardButtons] = useState<any[]>([]);
    const [climateRiskScoreButtons, setClimateRiskScoreButtons] = useState<any[]>([]);
    const [futureClimateRiskButtons, setFutureClimateRiskButtons] = useState<any[]>([]);
    const [climateRiskVariableButtons, setClimateRiskVariableButtons] = useState<any[]>([]);
    const [selectedButtons, setSelectedButtons] = useState<string[][]>(
        context.singleAnalyseModal?.selectId
            ? [[context.singleAnalyseModal.selectId], [], [], []]
            : [[], [], [], []]
    );
    const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);

    const hasPermission = (id: string) =>
        context.menu.some((item) => item.id === id && item.status === "1");

    const hasHazardPermission = hasPermission("102001") || hasPermission("102004");
    const hasClimateModulePermission = hasPermission("102002");
    const hasClimateRiskScorePermission = hasClimateModulePermission && hasPermission("102006");
    const hasFutureClimateRiskPermission = hasClimateModulePermission && hasPermission("102007");
    const hasClimateRiskVariablePermission = hasClimateModulePermission && hasPermission("102008");

    const climateSubOptionItems = [
        hasClimateRiskScorePermission
            ? { value: CLIMATE_RISK_SCORE_SUB_OPTION, label: t("options.single.climateRiskScores") }
            : null,
        hasFutureClimateRiskPermission
            ? { value: FUTURE_CLIMATE_RISK_SUB_OPTION, label: t("options.single.futureHazards") }
            : null,
        hasClimateRiskVariablePermission
            ? { value: CLIMATE_RISK_VARIABLE_SUB_OPTION, label: t("options.single.climateVariables") }
            : null,
    ].filter(Boolean) as { value: SingleSubOption; label: string }[];

    const subOptionItems =
        option === Option.HAZARD
            ? [{ value: NATURAL_HAZARD_SUB_OPTION, label: t("options.single.hazards") }]
            : climateSubOptionItems;

    const firstClimateSubOption = climateSubOptionItems[0]?.value;

    const currentClimateButtons =
        subOption === FUTURE_CLIMATE_RISK_SUB_OPTION
            ? futureClimateRiskButtons
            : subOption === CLIMATE_RISK_VARIABLE_SUB_OPTION
                ? climateRiskVariableButtons
                : climateRiskScoreButtons;

    const getSelectLayerIndex = (): number => {
        if (option === Option.HAZARD) return 0;
        if (subOption === CLIMATE_RISK_SCORE_SUB_OPTION) return 1;
        if (subOption === FUTURE_CLIMATE_RISK_SUB_OPTION) return 2;
        if (subOption === CLIMATE_RISK_VARIABLE_SUB_OPTION) return 3;
        return 1; // 切换层级后的过渡帧默认使用 climate-risk-score
    };

    const close = () => {
        dispatch({ type: AppAction.setSingleAnalyseModal, payload: undefined });
        handleRevert();
    };

    const onOpen = () => {
        dispatch({ type: AppAction.setSelectLayer, payload: selectedButtons });
        
        // 合并之前的选择和当前选择，保留多种分析类型的结果
        const previousSelectedLayers = context.singleAnalysisSelectedLayers || [[], [], [], []];
        const mergedSelectedLayers = [
            selectedButtons[0].length ? selectedButtons[0] : previousSelectedLayers[0],
            selectedButtons[1].length ? selectedButtons[1] : previousSelectedLayers[1],
            selectedButtons[2].length ? selectedButtons[2] : previousSelectedLayers[2],
            selectedButtons[3].length ? selectedButtons[3] : previousSelectedLayers[3],
        ];
        
        dispatch({ type: AppAction.setSingleAnalysisSelectedLayers, payload: mergedSelectedLayers });
        dispatch({ type: AppAction.setSelectedEvent, payload: selectedEvent });
        if (selectedButtons[0].length && !context.askAgain) {
            const fathomLayer = selectedButtons[0].find((e) => FATHOM_LAYER_IDS.includes(e));
            if (fathomLayer) {
                close();
                dispatch({ type: AppAction.setSingleFathomPopup, payload: "locations" });
                return;
            }
        }
        runAnalysis();
    };

    const runAnalysis = async (selBtns = selectedButtons, _selEvt = selectedEvent) => {
        const params: SingleAnalysisParams = {
            eventLayers: [],
            catNetLayers: [],
            responseStructure: [],
            climateRiskScores: [],
            longitude: undefined,
            latitude: undefined,
            country: undefined,
            oversea: undefined,
            spatial: undefined,
        };

        if (context.currentMultipleLocationInfo) {
            params.longitude = context.currentMultipleLocationInfo.longitudeWgs;
            params.latitude = context.currentMultipleLocationInfo.latitudeWgs;
            params.country = context.currentMultipleLocationInfo.country;
            params.spatial = "wgs";
        } else {
            params.longitude = context.searchLocationInfo?.[context.language]?.location?.split(",")[0];
            params.latitude = context.searchLocationInfo?.[context.language]?.location?.split(",")[1];
            params.country = context.searchLocationInfo?.[context.language]?.country;
            params.oversea = context.searchLocationInfo?.[context.language]?.oversea;
            params.spatial = context.searchLocationInfo?.[context.language]?.spatial;
        }

        if (selBtns[0].length) {
            params.catNetLayers = selBtns[0];
        }

        if (selBtns[1].length) {
            params.climateRiskScores = buildClimateRiskScores(selBtns[1]);
            params.responseStructure = [CRS_RESPONSE_STRUCTURE];
        }

        if (selBtns[2].length) {
            params.futureHazardRiskScores = buildFutureHazardRiskScores(selBtns[2]);
            params.responseStructure = [CRS_RESPONSE_STRUCTURE];
        }

        if (selBtns[3].length) {
            params.crsComponentRiskScores = buildCrsComponentRiskScores(selBtns[3]);
            params.responseStructure = [CRS_RESPONSE_STRUCTURE];
        }

        syncSingleAnalysisRunningStatus(
            selBtns,
            dispatch,
            context.climateRiskCategorySspData
        );
        executeSingleAnalysisWithReporting(params, selBtns, context.hazardLegends, dispatch);

        dispatch({ type: AppAction.setSingleLocationAnalysed, payload: true });
        dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: false });
        close();
    };

    /* tryAgain 触发后重新发起分析 */
    useEffect(() => {
        if (context.tryAgainType === "analyse" && context.rightPanel === RightPanel.Single) {
            runAnalysis(context.selectLayer, context.selectedEvent);
        }
    }, [context.tryAgainType]);

    /* 构建 hazard 请求参数并发起查询（102001/102004） */
    useEffect(() => {
        if (!context?.layerList?.length) return;
        const hazardLegendsIds: string[] = [];
        const hazardPermission = context.menu.find((item) => item.id === "102001" && item.status === "1");
        const fathomPermission = context.menu.find((item) => item.id === "102004" && item.status === "1");

        const arr =
            context?.layerList
                ?.map((e) => e.children)
                .flat()
                .map((e) => {
                    hazardLegendsIds.push(e.id);
                    const isFathom = e.id.includes("Fathom");
                    return {
                        id: e.id,
                        title: e.name,
                        validateStatus: isFathom ? fathomValidateStatus : hazardValidateStatus,
                        disabled: isFathom ? !fathomPermission || fathomValidateStatus : !hazardPermission || hazardValidateStatus,
                        icon: Svg({
                            icon: e.id,
                            disabled: isFathom ? !fathomPermission || fathomValidateStatus : !hazardPermission || hazardValidateStatus,
                        }),
                    };
                }) || [];
        setHazardButtons(arr);

        if (!context.hazardLegends) {
            dispatch({ type: AppAction.setHazardLegends, payload: [] });
            getLegends({ layers: hazardLegendsIds.join(",") }).then((res: any) => {
                dispatch({ type: AppAction.setHazardLegends, payload: res.data.legends });
            });
        }
    }, [context.menu, context.singleAnalyseModal, context.layerList, fathomValidateStatus, hazardValidateStatus]);

    /* 构建 climate 请求参数并发起查询（102002 + 102006/102007/102008） */
    useEffect(() => {
        // 对 CRS 使用 layers.item.* 翻译键
        setClimateRiskScoreButtons(
            (context?.crsLayerList || []).map((e) => ({
                id: e.id,
                title: e.name,
                validateStatus: crsValidateStatus,
                disabled: !hasClimateRiskScorePermission || crsValidateStatus,
                icon: Svg({ icon: e.id, disabled: !hasClimateRiskScorePermission || crsValidateStatus }),
            }))
        );

        // 对 futureHazard 使用 futureHazard.* 翻译键
        setFutureClimateRiskButtons(
            (context?.futureHazardLayerList || []).map((e: any) => ({
                id: e.id,
                title: `futureHazard.${e.id}`,
                validateStatus: crsValidateStatus,
                disabled: !hasFutureClimateRiskPermission || crsValidateStatus,
                icon: Svg({ icon: e.id, disabled: !hasFutureClimateRiskPermission || crsValidateStatus }),
            }))
        );

        // 对 CRSComponent 使用 CRSComponent.* 翻译键
        setClimateRiskVariableButtons(
            (context?.crsComponentLayerList || []).map((e: any) => ({
                id: e.id,
                title: `CRSComponent.${e.id}`,
                validateStatus: crsValidateStatus,
                disabled: !hasClimateRiskVariablePermission || crsValidateStatus,
                icon: Svg({ icon: e.id, disabled: !hasClimateRiskVariablePermission || crsValidateStatus }),
            }))
        );
    }, [
        hasClimateRiskScorePermission,
        hasFutureClimateRiskPermission,
        hasClimateRiskVariablePermission,
        context.crsLayerList,
        context.futureHazardLayerList,
        context.crsComponentLayerList,
        crsValidateStatus,
    ]);

    useEffect(() => {
        if (option === Option.HAZARD) {
            setSubOption(NATURAL_HAZARD_SUB_OPTION);
            return;
        }
        // 切换到 CLIMATE 或权限变化时，确保 subOption 在可用项中
        if (option === Option.CLIMATE && firstClimateSubOption) {
            const isCurrentSubOptionValid = climateSubOptionItems.some((item) => item.value === subOption);
            if (!isCurrentSubOptionValid) {
                setSubOption(firstClimateSubOption);
            }
        }
    }, [option, climateSubOptionItems, firstClimateSubOption, subOption]);

    /* 弹窗打开后默认选中上次分析的条目 */
    useEffect(() => {
        if (!context.menu?.length || context.singleAnalyseModal === undefined) return;

        const locations: string[] = [];
        if (context.currentMultipleLocationInfo) {
            locations.push(
                `${context.currentMultipleLocationInfo.longitudeWgs},${context.currentMultipleLocationInfo.latitudeWgs}`
            );
        } else if (context.searchLocationInfo) {
            const loc = context.searchLocationInfo?.[context.language]?.location?.split(",");
            if (loc) locations.push(`${loc[0]},${loc[1]}`);
        }

        getLayerCounter({ actionCategories: "fathom,nonFathom,crs" }, locations).then((res) => {
            setFathomValidateStatus(
                !res.data.existInFathomCache &&
                res.data.itemFathomQuotas !== -1 &&
                (res.data.itemFathomQuotas ?? 0) <= (res.data.trItemFathomCount ?? 0)
            );
            setHazardValidateStatus(
                !res.data.existInCache &&
                res.data.itemQuotas !== -1 &&
                (res.data.itemQuotas ?? 0) <= (res.data.trItemCount ?? 0)
            );
            setCrsValidateStatus(
                !res.data.existInCRSCache &&
                res.data.itemCRSQuotas !== -1 &&
                (res.data.itemCRSQuotas ?? 0) <= (res.data.trItemCRSCount ?? 0)
            );
        });

        setSelectedButtons(
            context.singleAnalyseModal?.selectId
                ? [[context.singleAnalyseModal.selectId], [], [], []]
                : [[], [], [], []]
        );

        if (hasHazardPermission) {
            setOption(Option.HAZARD);
            setSubOption(NATURAL_HAZARD_SUB_OPTION);
            return;
        }
        if (firstClimateSubOption) {
            setOption(Option.CLIMATE);
            setSubOption(firstClimateSubOption);
        }
    }, [context.menu, context.singleAnalyseModal, hasHazardPermission, firstClimateSubOption]);

    const handleRevert = () => {
        setSelectedButtons([[], [], [], []]);
        setSelectedEvent(null);
    };

    const isAnySelected = selectedButtons.flatMap((arr) => arr).length || !!selectedEvent;

    return (
        <Dialog
            open={!!context.singleAnalyseModal}
            onClose={close}
            sx={{
                "& .MuiDialog-paper": {
                    width: "780px",
                    maxWidth: "80vw",
                    maxHeight: "85vh",
                },
            }}
        >
            <div className={styles.container}>
                <div className={styles.header}>{t("analyseModal.analyseLocations")}</div>
                <div className={styles.analyse}>
                    <div>
                        <div className={styles.header}>
                            <LocationsSvg className={styles.image} />
                            <div>
                                <div className={styles.headerSingleName}>
                                    {context.currentMultipleLocationInfo
                                        ? context.currentMultipleLocationInfo.name
                                        : context.searchLocationInfo?.[context.language]?.formatted_address}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 mb-2">
                        <Options selected={option} onChange={setOption} />
                    </div>
                    <div className="mt-5 mb-2">
                        <SubOptions
                            selected={subOption}
                            onChange={(value) => setSubOption(value as SingleSubOption)}
                            customItems={subOptionItems}
                        />
                    </div>
                    <div className={styles.tabContent}>
                        {option === Option.HAZARD && (
                            <SelectLayer
                                index={0}
                                buttons={hazardButtons}
                                selectedButtons={selectedButtons}
                                setSelectedButtons={setSelectedButtons}
                                type="single"
                            />
                        )}
                        {option === Option.CLIMATE && (
                            <SelectLayer
                                index={getSelectLayerIndex()}
                                buttons={currentClimateButtons}
                                selectedButtons={selectedButtons}
                                setSelectedButtons={setSelectedButtons}
                                type="single"
                            />
                        )}
                    </div>
                </div>
                <div className="flex justify-between mt-5 mb-5">
                    <div>
                        <Button onClick={handleRevert} variant="text" color="success">
                            {t("buttons.reset")}
                        </Button>
                    </div>
                    <div>
                        <Button onClick={close} variant="text" color="success">
                            {t("buttons.close")}
                        </Button>
                        <Button
                            color="success"
                            variant="contained"
                            disabled={!isAnySelected}
                            onClick={() => onOpen()}
                        >
                            {t("buttons.runAnalysis")}
                        </Button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

export default SingleAnalyseModal;


